import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import globalSettingsStores from '../../../../../stores/settings-stores';
import globalUserPage from '../../../../../stores/global-user-page';
import {
  PaymentMethod,
  AllocationWithAmount,
  CheckPaymentDetails,
} from '../payment-method-selection-dialog';
import { CreditCardTokenEntity } from '../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { CreditCardSelection } from './credit-card-selection';
import { PaymentInstallmentStatus } from '../../../../../api/types/payments/installments/entities';
import { StudentScholarshipsStatus } from '../../../../../api/types/students/student-scholarships/student-scholarship-entity';
import { AddCheckDialog } from './add-check-dialog';
import { AddCheckSeriesDialog } from './add-check-series-dialog';
import { bucketFillCheckAllocations } from '../utils/bucket-fill-check-allocations';
import {
  addMonthsToDate,
  referenceIdForSeriesIndex,
} from '../utils/generate-check-series';

// Helper function to calculate the default first payment month/year
const calculateDefaultFirstPaymentMonth = (): {
  month: number;
  year: number;
} => {
  const today = new Date();
  const currentDay = today.getDate();
  const year = today.getFullYear();
  const month = today.getMonth();

  // If today is before the 20th, use current month
  // If today is on or after the 20th, use next month
  if (currentDay < 20) {
    return { month, year };
  } else {
    // Move to next month
    const nextMonth = month + 1;
    if (nextMonth > 11) {
      return { month: 0, year: year + 1 };
    }
    return { month: nextMonth, year };
  }
};

// Generate available months for selection (current or next month through 12 months ahead)
const generateAvailableMonths = (): {
  label: string;
  value: { month: number; year: number };
}[] => {
  const defaultMonth = calculateDefaultFirstPaymentMonth();
  const options: { label: string; value: { month: number; year: number } }[] =
    [];

  let currentMonth = defaultMonth.month;
  let currentYear = defaultMonth.year;

  for (let i = 0; i < 12; i++) {
    const monthLabel = String(currentMonth + 1).padStart(2, '0');
    options.push({
      label: `${monthLabel}/${currentYear}`,
      value: { month: currentMonth, year: currentYear },
    });

    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }
  }

  return options;
};

interface PaymentMethodFormProps {
  selectedMethod: PaymentMethod;
  allocationAmounts: AllocationWithAmount[];
  // Credit Card
  creditCardTokens: CreditCardTokenEntity[];
  creditCardData: {
    selectedCreditCardTokenId: number | null;
    numberOfInstallments: number;
  };
  onCreditCardDataChange: (data: {
    selectedCreditCardTokenId: number | null;
    numberOfInstallments: number;
  }) => void;
  onAddNewCardClick: () => void;
  // Bank Transfer
  bankTransferData: {
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    transferDate: Date | null;
    referenceId: string;
  };
  onBankTransferDataChange: (data: {
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    transferDate: Date | null;
    referenceId: string;
  }) => void;
  // Check
  checkData: {
    checkNumber: string;
    amount: number | null;
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    clearingDate: Date | null;
  };
  onCheckDataChange: (data: {
    checkNumber: string;
    amount: number | null;
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    clearingDate: Date | null;
  }) => void;
  checks: CheckPaymentDetails[];
  onChecksChange: (checks: CheckPaymentDetails[]) => void;
  // Scholarship
  scholarshipData: {
    scholarshipId: number | null;
    studentScholarshipId: number | null;
  };
  onScholarshipDataChange: (data: {
    scholarshipId: number | null;
    studentScholarshipId: number | null;
  }) => void;
  // Credit Card Installments
  installmentsData: {
    numberOfInstallments: number;
    selectedCreditCardTokenId: number | null;
    firstPaymentMonth: { month: number; year: number } | null;
  };
  onInstallmentsDataChange: (data: {
    numberOfInstallments: number;
    selectedCreditCardTokenId: number | null;
    firstPaymentMonth: { month: number; year: number } | null;
  }) => void;
  // Bank Transfer Installments
  bankTransferInstallmentsData: {
    numberOfInstallments: number;
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    firstPaymentMonth: { month: number; year: number } | null;
  };
  onBankTransferInstallmentsDataChange: (data: {
    numberOfInstallments: number;
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    firstPaymentMonth: { month: number; year: number } | null;
  }) => void;
  // Existing Installments
  existingCreditCardInstallments: any[];
  selectedExistingInstallmentPlanId: number | null;
  onSelectedExistingInstallmentPlanIdChange: (id: number | null) => void;
  existingBankTransferInstallments: any[];
  selectedExistingBankTransferPlanId: number | null;
  onSelectedExistingBankTransferPlanIdChange: (id: number | null) => void;
}

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  selectedMethod,
  allocationAmounts,
  creditCardTokens,
  creditCardData,
  onCreditCardDataChange,
  onAddNewCardClick,
  bankTransferData,
  onBankTransferDataChange,
  checkData,
  onCheckDataChange,
  checks,
  onChecksChange,
  scholarshipData,
  onScholarshipDataChange,
  installmentsData,
  onInstallmentsDataChange,
  bankTransferInstallmentsData,
  onBankTransferInstallmentsDataChange,
  existingCreditCardInstallments,
  selectedExistingInstallmentPlanId,
  onSelectedExistingInstallmentPlanIdChange,
  existingBankTransferInstallments,
  selectedExistingBankTransferPlanId,
  onSelectedExistingBankTransferPlanIdChange,
}) => {
  const [showAddCheckDialog, setShowAddCheckDialog] = React.useState(false);
  const [showCheckSeriesDialog, setShowCheckSeriesDialog] =
    React.useState(false);
  const [editingCheckIndex, setEditingCheckIndex] = React.useState<
    number | null
  >(null);
  const formContainerStyle = {
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  };

  const inputStyle = {
    width: '100%',
    marginBottom: '1rem',
  };

  const formatDate = (date?: Date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('he-IL');
  };

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  /** Block adding checks while any שירות תשלום row is still 0 / unset (matches allocation InputNumbers above). */
  const blockCheckButtonsDueToAllocationAmounts = React.useMemo(() => {
    if (allocationAmounts.length === 0) return true;
    return allocationAmounts.some((a) => (a.amount ?? 0) <= 0);
  }, [allocationAmounts]);

  const checkButtonsBlockedTooltip =
    'יש למלא סכום חיוב גדול מ-0 לכל שירותי התשלום לפני הוספת המחאות';

  // Helper function to calculate the first payment date (20th of the closest month)
  const calculateFirstPaymentDate = (): Date => {
    // If user selected a specific month, use that
    if (installmentsData.firstPaymentMonth) {
      return new Date(
        installmentsData.firstPaymentMonth.year,
        installmentsData.firstPaymentMonth.month,
        20,
      );
    }
    // Otherwise use the default
    const defaultMonth = calculateDefaultFirstPaymentMonth();
    return new Date(defaultMonth.year, defaultMonth.month, 20);
  };

  switch (selectedMethod) {
    case PaymentMethod.CreditCard:
      return (
        <div style={formContainerStyle}>
          <div className="flex flex-col gap-4">
            {/* Credit Card Selection */}
            <CreditCardSelection
              creditCardTokens={creditCardTokens}
              selectedCreditCardTokenId={
                creditCardData.selectedCreditCardTokenId
              }
              onCreditCardChange={(tokenId) =>
                onCreditCardDataChange({
                  ...creditCardData,
                  selectedCreditCardTokenId: tokenId,
                })
              }
              onAddNewCardClick={onAddNewCardClick}
            />

            {/* Number of installments (1-10) */}
            <div className="flex flex-col" style={{ maxWidth: '250px' }}>
              <label className="mb-2 font-medium">מספר תשלומים</label>
              <Dropdown
                value={creditCardData.numberOfInstallments}
                options={Array.from({ length: 10 }, (_, i) => ({
                  label: `${i + 1} תשלומים`,
                  value: i + 1,
                }))}
                onChange={(e) =>
                  onCreditCardDataChange({
                    ...creditCardData,
                    numberOfInstallments: e.value,
                  })
                }
                style={inputStyle}
                placeholder="בחר מספר תשלומים"
              />
            </div>
          </div>
        </div>
      );

    case PaymentMethod.BankTransfer:
      return (
        <div style={formContainerStyle}>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col">
              <label className="mb-2 font-medium">בנק</label>
              <InputText
                style={inputStyle}
                value={bankTransferData.bankNumber}
                onChange={(e) =>
                  onBankTransferDataChange({
                    ...bankTransferData,
                    bankNumber: e.target.value.replace(/\D/g, ''),
                  })
                }
                keyfilter="int"
                inputMode="numeric"
                placeholder="הזן מספר בנק"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">סניף</label>
              <InputText
                style={inputStyle}
                value={bankTransferData.branchNumber}
                onChange={(e) =>
                  onBankTransferDataChange({
                    ...bankTransferData,
                    branchNumber: e.target.value.replace(/\D/g, ''),
                  })
                }
                keyfilter="int"
                inputMode="numeric"
                placeholder="הזן מספר סניף"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">חשבון</label>
              <InputText
                style={inputStyle}
                value={bankTransferData.accountNumber}
                onChange={(e) =>
                  onBankTransferDataChange({
                    ...bankTransferData,
                    accountNumber: e.target.value.replace(/\D/g, ''),
                  })
                }
                keyfilter="int"
                inputMode="numeric"
                placeholder="הזן מספר חשבון"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">תאריך פרעון</label>
              <Calendar
                style={{ direction: 'ltr', ...inputStyle }}
                value={bankTransferData.transferDate}
                onChange={(e) =>
                  onBankTransferDataChange({
                    ...bankTransferData,
                    transferDate: e.value as Date,
                  })
                }
                inputStyle={{ direction: 'rtl' }}
                showIcon
                iconPos={'right'}
                dateFormat="dd/mm/yy"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">
                מספר אסמכתא{' '}
                <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                  (אופציונלי)
                </span>
              </label>
              <InputText
                style={inputStyle}
                value={bankTransferData.referenceId}
                onChange={(e) =>
                  onBankTransferDataChange({
                    ...bankTransferData,
                    referenceId: e.target.value,
                  })
                }
                maxLength={30}
                placeholder="הזן מספר אסמכתא (עד 30 תווים)"
              />
            </div>
          </div>
        </div>
      );

    case PaymentMethod.Check:
      return (
        <div style={formContainerStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '16px',
            }}
          >
            <div>
              <div
                style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}
              >
                המחאות לתשלום
              </div>
              <div
                style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}
              >
                ניתן להוסיף כמה המחאות לאותו תשלום. כל המחאה תישלח כפריט נפרד.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                type="button"
                icon="pi pi-plus"
                className="p-button-sm p-button-raised"
                aria-label="הוסף המחאה"
                disabled={blockCheckButtonsDueToAllocationAmounts}
                tooltip={
                  blockCheckButtonsDueToAllocationAmounts
                    ? checkButtonsBlockedTooltip
                    : 'הוסף המחאה'
                }
                tooltipOptions={{
                  position: 'top',
                  showOnDisabled: true,
                }}
                onClick={() => setShowAddCheckDialog(true)}
              />
              {/*<Button
                type="button"
                icon="pi pi-list"
                className="p-button-sm p-button-outlined"
                aria-label="סדרת המחאות"
                disabled={blockCheckButtonsDueToAllocationAmounts}
                tooltip={
                  blockCheckButtonsDueToAllocationAmounts
                    ? checkButtonsBlockedTooltip
                    : 'סדרת המחאות'
                }
                tooltipOptions={{
                  position: 'top',
                  showOnDisabled: true,
                }}
                onClick={() => setShowCheckSeriesDialog(true)}
              />*/}
            </div>
          </div>

          {checks.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '12px',
                marginBottom: '18px',
              }}
            >
              {checks.map((c, idx) => (
                <div
                  key={`${c.referenceId}-${idx}`}
                  style={{
                    background:
                      'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '12px 12px',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '10px',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 800,
                          color: '#111827',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        המחאה #{c.referenceId}
                      </div>
                      <div
                        style={{
                          marginTop: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          borderRadius: '999px',
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          fontSize: '12px',
                          fontWeight: 800,
                          color: '#0f172a',
                        }}
                      >
                        <i className="pi pi-money-bill" aria-hidden />₪
                        {Number(c.amount || 0).toLocaleString('he-IL')}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginTop: '2px',
                        }}
                      >
                        {c.dueDate
                          ? `תאריך פירעון: ${formatDate(c.dueDate)}`
                          : 'תאריך פירעון: לא צוין'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <Button
                        type="button"
                        icon="pi pi-pencil"
                        className="p-button-text p-button-sm"
                        tooltip="עריכת המחאה"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => {
                          setEditingCheckIndex(idx);
                          setShowAddCheckDialog(true);
                        }}
                      />
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger p-button-sm"
                        tooltip="הסר המחאה"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => {
                          const next = checks.filter((_, i) => i !== idx);
                          onChecksChange(next);
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: '8px 10px',
                      fontSize: '12px',
                      color: '#334155',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#64748b' }}>בנק</div>
                      <div
                        style={{
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.bankNumber}
                      </div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#64748b' }}>סניף</div>
                      <div
                        style={{
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.branchNumber}
                      </div>
                    </div>
                    <div style={{ minWidth: 0, gridColumn: '1 / -1' }}>
                      <div style={{ color: '#64748b' }}>חשבון</div>
                      <div
                        style={{
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.accountNumber}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: '10px',
                      borderTop: '1px dashed #e2e8f0',
                      paddingTop: '10px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        gap: '10px',
                      }}
                    >
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>
                        פיצול לפי שירותי תשלום
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        סה״כ: ₪
                        {round2(
                          c.allocations?.reduce(
                            (s, a) => s + (a.amount || 0),
                            0,
                          ) || 0,
                        ).toLocaleString('he-IL')}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {allocationAmounts.map((alloc) => {
                        const existing =
                          c.allocations?.find(
                            (a) => a.allocationId === alloc.allocationId,
                          )?.amount ?? 0;

                        return (
                          <div
                            key={alloc.allocationId}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '10px',
                              padding: '8px 10px',
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: '12px',
                                fontWeight: 700,
                                color: '#0f172a',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={alloc.paymentServiceName}
                            >
                              {alloc.paymentServiceName}
                            </div>

                            <div
                              style={{
                                flexShrink: 0,
                                width: '118px',
                                maxWidth: '40%',
                              }}
                            >
                              <InputNumber
                                value={existing}
                                prefix="₪"
                                locale="he-IL"
                                min={0}
                                maxFractionDigits={2}
                                inputStyle={{
                                  textAlign: 'right',
                                  width: '100%',
                                }}
                                style={{ width: '100%', direction: 'ltr' }}
                                onChange={(e) => {
                                  const nextVal = round2(e.value || 0);
                                  const nextChecks = checks.map((chk, i) => {
                                    if (i !== idx) return chk;
                                    const nextAllocations = [
                                      ...(chk.allocations ?? []),
                                    ].filter(
                                      (a) =>
                                        a.allocationId !== alloc.allocationId,
                                    );

                                    if (nextVal > 0) {
                                      nextAllocations.push({
                                        allocationId: alloc.allocationId,
                                        amount: nextVal,
                                      });
                                    }

                                    return {
                                      ...chk,
                                      allocations: nextAllocations,
                                    };
                                  });
                                  onChecksChange(nextChecks);
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <AddCheckDialog
            visible={showAddCheckDialog}
            onHide={() => {
              setShowAddCheckDialog(false);
              setEditingCheckIndex(null);
            }}
            initialDraft={(() => {
              if (editingCheckIndex !== null) {
                const editing = checks[editingCheckIndex];
                return {
                  referenceId: editing?.referenceId ?? '',
                  amount: editing?.amount ?? null,
                  bankNumber: editing?.bankNumber ?? '',
                  branchNumber: editing?.branchNumber ?? '',
                  accountNumber: editing?.accountNumber ?? '',
                  dueDate: editing?.dueDate,
                };
              }
              // Serial-entry defaults: when adding a NEW check and at least
              // one check has already been entered, increment the reference
              // id, push the due date forward by one month, reset amount to 0
              // and keep bank/branch/account from the last check.
              const lastCheck =
                checks.length > 0 ? checks[checks.length - 1] : null;
              if (lastCheck) {
                return {
                  referenceId: referenceIdForSeriesIndex(
                    lastCheck.referenceId ?? '',
                    1,
                  ),
                  amount: 0,
                  bankNumber: lastCheck.bankNumber ?? '',
                  branchNumber: lastCheck.branchNumber ?? '',
                  accountNumber: lastCheck.accountNumber ?? '',
                  dueDate: lastCheck.dueDate
                    ? addMonthsToDate(lastCheck.dueDate, 1)
                    : undefined,
                };
              }
              // First check ever in this payment: fall back to whatever the
              // user previously typed into the form-level checkData snapshot.
              return {
                referenceId: checkData.checkNumber ?? '',
                amount: checkData.amount ?? null,
                bankNumber: checkData.bankNumber ?? '',
                branchNumber: checkData.branchNumber ?? '',
                accountNumber: checkData.accountNumber ?? '',
                dueDate: checkData.clearingDate ?? undefined,
              };
            })()}
            headerText={
              editingCheckIndex !== null ? 'עריכת המחאה' : 'הוספת המחאה'
            }
            submitLabel={editingCheckIndex !== null ? 'שמור' : 'הוסף'}
            submitIcon={
              editingCheckIndex !== null ? 'pi pi-check' : 'pi pi-plus'
            }
            onSubmit={(c) => {
              if (editingCheckIndex !== null) {
                const next = checks.map((item, i) =>
                  i === editingCheckIndex
                    ? {
                        ...c,
                        // keep existing allocations unless user wants to adjust manually
                        allocations: item.allocations ?? [],
                      }
                    : item,
                );
                onChecksChange(next);
              } else {
                const alreadyAllocatedByAllocationId: Record<number, number> =
                  {};
                for (const chk of checks) {
                  for (const a of chk.allocations ?? []) {
                    alreadyAllocatedByAllocationId[a.allocationId] = round2(
                      (alreadyAllocatedByAllocationId[a.allocationId] || 0) +
                        (a.amount || 0),
                    );
                  }
                }

                const allocations = bucketFillCheckAllocations({
                  allocationTotals: allocationAmounts,
                  checkAmount: c.amount,
                  alreadyAllocatedByAllocationId,
                });
                onChecksChange([...checks, { ...c, allocations }]);
              }
              onCheckDataChange({
                checkNumber: c.referenceId,
                amount: c.amount,
                bankNumber: c.bankNumber,
                branchNumber: c.branchNumber,
                accountNumber: c.accountNumber,
                clearingDate: c.dueDate ?? null,
              });
              setEditingCheckIndex(null);
            }}
          />

          <AddCheckSeriesDialog
            visible={showCheckSeriesDialog}
            onHide={() => setShowCheckSeriesDialog(false)}
            allocationAmounts={allocationAmounts}
            existingChecks={checks}
            initialBank={{
              bankNumber: checkData.bankNumber ?? '',
              branchNumber: checkData.branchNumber ?? '',
              accountNumber: checkData.accountNumber ?? '',
            }}
            onSubmit={(generated) => {
              onChecksChange([...checks, ...generated]);
              const last = generated[generated.length - 1];
              if (last) {
                onCheckDataChange({
                  checkNumber: last.referenceId,
                  amount: last.amount,
                  bankNumber: last.bankNumber,
                  branchNumber: last.branchNumber,
                  accountNumber: last.accountNumber,
                  clearingDate: last.dueDate ?? null,
                });
              }
            }}
          />
        </div>
      );

    case PaymentMethod.Scholarship:
      // Calculate total payment amount
      const totalPaymentAmount = allocationAmounts.reduce(
        (sum, alloc) => sum + (alloc.amount || 0),
        0,
      );

      return (
        <div style={formContainerStyle}>
          <div className="flex flex-col">
            <label className="mb-2 font-medium">בחר מלגה תלמיד</label>
            <Dropdown
              style={inputStyle}
              value={scholarshipData.studentScholarshipId}
              options={
                globalUserPage.studentDetails?.studentScholarships
                  ?.filter((scholarship) => {
                    // Filter by status (approved or pending)
                    const hasValidStatus =
                      scholarship.studentScholarshipsStatus ===
                        StudentScholarshipsStatus.APPROVED ||
                      scholarship.studentScholarshipsStatus ===
                        StudentScholarshipsStatus.PENDING;

                    // Filter by sufficient balance
                    const remaining =
                      scholarship.scholarshipSum - scholarship.consumeSum;
                    const hasEnoughBalance = remaining >= totalPaymentAmount;

                    return hasValidStatus && hasEnoughBalance;
                  })
                  ?.map((scholarship) => {
                    const remaining =
                      scholarship.scholarshipSum - scholarship.consumeSum;
                    return {
                      label: `${scholarship.funderName} - סכום: ₪${scholarship.scholarshipSum.toLocaleString()} | נותר: ₪${remaining.toLocaleString()}`,
                      value: scholarship.id,
                    };
                  }) ?? []
              }
              onChange={(e) => {
                const selected =
                  globalUserPage.studentDetails?.studentScholarships?.find(
                    (s) => s.id === e.value,
                  );
                onScholarshipDataChange({
                  /*...scholarshipData,*/
                  studentScholarshipId: selected.id,
                  scholarshipId: selected?.funderId,
                });
              }}
              placeholder="בחר מלגה המשויכת לתלמיד"
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#E3F2FD',
                borderRadius: '6px',
                border: '1px solid #90CAF9',
              }}
            >
              <i
                className="pi pi-info-circle"
                style={{
                  color: '#1976D2',
                  fontSize: '16px',
                  marginTop: '2px',
                }}
              />
              <span
                style={{
                  fontSize: '14px',
                  color: '#1976D2',
                  lineHeight: '1.5',
                }}
              >
                ניתן לבחור רק מלגות עם סטטוס "מאושרת" או "ממתינה" ועם יתרה מספקת
                לתשלום. סכום התשלום הנוכחי: ₪
                {totalPaymentAmount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );

    case PaymentMethod.CreditCardInstallments:
      const firstPaymentDate = calculateFirstPaymentDate();
      const totalAmount = allocationAmounts.reduce(
        (sum, alloc) => sum + (alloc.amount || 0),
        0,
      );
      const monthlyPayment =
        installmentsData.numberOfInstallments > 0
          ? totalAmount / installmentsData.numberOfInstallments
          : totalAmount;

      // Get default and available months
      const defaultFirstMonth = calculateDefaultFirstPaymentMonth();
      const availableMonths = generateAvailableMonths();
      const selectedFirstMonth =
        installmentsData.firstPaymentMonth || defaultFirstMonth;

      return (
        <div style={formContainerStyle}>
          <div className="flex flex-col gap-4">
            {/* Credit Card Selection */}
            <CreditCardSelection
              creditCardTokens={creditCardTokens}
              selectedCreditCardTokenId={
                installmentsData.selectedCreditCardTokenId
              }
              onCreditCardChange={(tokenId) =>
                onInstallmentsDataChange({
                  ...installmentsData,
                  selectedCreditCardTokenId: tokenId,
                })
              }
              onAddNewCardClick={onAddNewCardClick}
            />

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '16px',
                width: '100%',
                marginBottom: '16px',
              }}
            >
              <div style={{ flex: 1 }}>
                <label
                  className="mb-2 font-medium"
                  style={{ display: 'block', marginBottom: '8px' }}
                >
                  מספר תשלומים
                </label>
                <Dropdown
                  style={{ width: '100%' }}
                  value={installmentsData.numberOfInstallments}
                  options={Array.from({ length: 12 }, (_, i) => ({
                    label: `${i + 1} תשלומים`,
                    value: i + 1,
                  }))}
                  onChange={(e) =>
                    onInstallmentsDataChange({
                      ...installmentsData,
                      numberOfInstallments: e.value,
                    })
                  }
                  placeholder="בחר מספר תשלומים"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  className="mb-2 font-medium"
                  style={{ display: 'block', marginBottom: '8px' }}
                >
                  חודש תשלום ראשון
                </label>
                <Dropdown
                  style={{ width: '100%' }}
                  value={selectedFirstMonth}
                  options={availableMonths}
                  optionLabel="label"
                  onChange={(e) =>
                    onInstallmentsDataChange({
                      ...installmentsData,
                      firstPaymentMonth: e.value,
                    })
                  }
                  placeholder="בחר חודש תשלום ראשון"
                />
              </div>
            </div>

            {/* Friendly description for the user */}
            <div
              style={{
                backgroundColor: '#E3F2FD',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #90CAF9',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <i
                  className="pi pi-info-circle"
                  style={{ color: '#1976D2', fontSize: '20px' }}
                ></i>
                <span
                  style={{
                    fontWeight: 'bold',
                    color: '#1976D2',
                    fontSize: '16px',
                  }}
                >
                  פרטי הוראת הקבע
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  color: '#424242',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>סה"כ לתשלום:</span>
                  <span style={{ fontWeight: 'bold' }}>
                    ₪{totalAmount.toFixed(2)}
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>מספר תשלומים:</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {installmentsData.numberOfInstallments}
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>סכום תשלום חודשי:</span>
                  <span style={{ fontWeight: 'bold', color: '#1976D2' }}>
                    ₪{monthlyPayment.toFixed(2)}
                  </span>
                </div>
                <div
                  style={{
                    borderTop: '1px solid #90CAF9',
                    paddingTop: '8px',
                    marginTop: '4px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>תאריך חיוב ראשון:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {firstPaymentDate.toLocaleDateString('he-IL')}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '4px',
                    }}
                  >
                    <span>יום חיוב בכל חודש:</span>
                    <span style={{ fontWeight: 'bold' }}>20 לחודש</span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#FFF3E0',
                  borderRadius: '6px',
                  border: '1px solid #FFB74D',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <i
                    className="pi pi-calendar"
                    style={{
                      color: '#F57C00',
                      fontSize: '16px',
                      marginTop: '2px',
                    }}
                  ></i>
                  <div style={{ fontSize: '14px', color: '#E65100' }}>
                    <strong>לידיעתך:</strong> לאחר אישור ההוראה, יופיעו בדשבורד
                    הוראות הקבע שלך פירוט של כל התשלומים החודשיים, כולל תאריכי
                    החיוב והסכומים.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    case PaymentMethod.BankTransferInstallments:
      const bankInstallmentsTotalAmount = allocationAmounts.reduce(
        (sum, alloc) => sum + (alloc.amount || 0),
        0,
      );
      const bankInstallmentsMonthlyPayment =
        bankTransferInstallmentsData.numberOfInstallments > 0
          ? bankInstallmentsTotalAmount /
            bankTransferInstallmentsData.numberOfInstallments
          : bankInstallmentsTotalAmount;

      // Get default and available months for bank transfer
      const bankDefaultFirstMonth = calculateDefaultFirstPaymentMonth();
      const bankAvailableMonths = generateAvailableMonths();
      const bankSelectedFirstMonth =
        bankTransferInstallmentsData.firstPaymentMonth || bankDefaultFirstMonth;
      const bankInstallmentsFirstPaymentDate =
        bankTransferInstallmentsData.firstPaymentMonth
          ? new Date(
              bankTransferInstallmentsData.firstPaymentMonth.year,
              bankTransferInstallmentsData.firstPaymentMonth.month,
              20,
            )
          : new Date(
              bankDefaultFirstMonth.year,
              bankDefaultFirstMonth.month,
              20,
            );

      return (
        <div style={formContainerStyle}>
          <div className="flex flex-col gap-4">
            {/* Bank Details Section */}
            <div
              style={{
                backgroundColor: '#E8EAF6',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #C5CAE9',
                marginBottom: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <i
                  className="pi pi-building"
                  style={{ color: '#3F51B5', fontSize: '20px' }}
                ></i>
                <span
                  style={{
                    fontWeight: 'bold',
                    color: '#3F51B5',
                    fontSize: '16px',
                  }}
                >
                  פרטי חשבון בנק
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="mb-2 font-medium">בנק</label>
                  <InputText
                    style={inputStyle}
                    value={bankTransferInstallmentsData.bankNumber}
                    onChange={(e) =>
                      onBankTransferInstallmentsDataChange({
                        ...bankTransferInstallmentsData,
                        bankNumber: e.target.value.replace(/\D/g, ''),
                      })
                    }
                    keyfilter="int"
                    inputMode="numeric"
                    placeholder="הזן מספר בנק"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 font-medium">סניף</label>
                  <InputText
                    style={inputStyle}
                    value={bankTransferInstallmentsData.branchNumber}
                    onChange={(e) =>
                      onBankTransferInstallmentsDataChange({
                        ...bankTransferInstallmentsData,
                        branchNumber: e.target.value.replace(/\D/g, ''),
                      })
                    }
                    keyfilter="int"
                    inputMode="numeric"
                    placeholder="הזן מספר סניף"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 font-medium">חשבון</label>
                  <InputText
                    style={inputStyle}
                    value={bankTransferInstallmentsData.accountNumber}
                    onChange={(e) =>
                      onBankTransferInstallmentsDataChange({
                        ...bankTransferInstallmentsData,
                        accountNumber: e.target.value.replace(/\D/g, ''),
                      })
                    }
                    keyfilter="int"
                    inputMode="numeric"
                    placeholder="הזן מספר חשבון"
                  />
                </div>
              </div>
            </div>

            {/* Number of Installments and First Payment Month */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '16px',
                width: '100%',
                marginBottom: '16px',
              }}
            >
              <div style={{ flex: 1 }}>
                <label
                  className="mb-2 font-medium"
                  style={{ display: 'block', marginBottom: '8px' }}
                >
                  מספר תשלומים
                </label>
                <Dropdown
                  style={{ width: '100%' }}
                  value={bankTransferInstallmentsData.numberOfInstallments}
                  options={Array.from({ length: 12 }, (_, i) => ({
                    label: `${i + 1} תשלומים`,
                    value: i + 1,
                  }))}
                  onChange={(e) =>
                    onBankTransferInstallmentsDataChange({
                      ...bankTransferInstallmentsData,
                      numberOfInstallments: e.value,
                    })
                  }
                  placeholder="בחר מספר תשלומים"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  className="mb-2 font-medium"
                  style={{ display: 'block', marginBottom: '8px' }}
                >
                  חודש תשלום ראשון
                </label>
                <Dropdown
                  style={{ width: '100%' }}
                  value={bankSelectedFirstMonth}
                  options={bankAvailableMonths}
                  optionLabel="label"
                  onChange={(e) =>
                    onBankTransferInstallmentsDataChange({
                      ...bankTransferInstallmentsData,
                      firstPaymentMonth: e.value,
                    })
                  }
                  placeholder="בחר חודש תשלום ראשון"
                />
              </div>
            </div>

            {/* Friendly description for the user */}
            <div
              style={{
                backgroundColor: '#E3F2FD',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #90CAF9',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <i
                  className="pi pi-info-circle"
                  style={{ color: '#1976D2', fontSize: '20px' }}
                ></i>
                <span
                  style={{
                    fontWeight: 'bold',
                    color: '#1976D2',
                    fontSize: '16px',
                  }}
                >
                  פרטי הוראת הקבע
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  color: '#424242',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>סה"כ לתשלום:</span>
                  <span style={{ fontWeight: 'bold' }}>
                    ₪{bankInstallmentsTotalAmount.toFixed(2)}
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>מספר תשלומים:</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {bankTransferInstallmentsData.numberOfInstallments}
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>סכום תשלום חודשי:</span>
                  <span style={{ fontWeight: 'bold', color: '#1976D2' }}>
                    ₪{bankInstallmentsMonthlyPayment.toFixed(2)}
                  </span>
                </div>
                <div
                  style={{
                    borderTop: '1px solid #90CAF9',
                    paddingTop: '8px',
                    marginTop: '4px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>תאריך חיוב ראשון:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {bankInstallmentsFirstPaymentDate.toLocaleDateString(
                        'he-IL',
                      )}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '4px',
                    }}
                  >
                    <span>יום חיוב בכל חודש:</span>
                    <span style={{ fontWeight: 'bold' }}>20 לחודש</span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#FFF3E0',
                  borderRadius: '6px',
                  border: '1px solid #FFB74D',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <i
                    className="pi pi-calendar"
                    style={{
                      color: '#F57C00',
                      fontSize: '16px',
                      marginTop: '2px',
                    }}
                  ></i>
                  <div style={{ fontSize: '14px', color: '#E65100' }}>
                    <strong>לידיעתך:</strong> לאחר אישור ההוראה, יופיעו בדשבורד
                    הוראות הקבע שלך פירוט של כל התשלומים החודשיים, כולל תאריכי
                    החיוב והסכומים.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    case PaymentMethod.ExistingCreditCardInstallments:
      const newAmountToAdd = allocationAmounts.reduce(
        (sum, alloc) => sum + (alloc.amount || 0),
        0,
      );
      const selectedCreditCardPlan = existingCreditCardInstallments.find(
        (plan) => plan.installmentPlan.id === selectedExistingInstallmentPlanId,
      );
      const remainingPaymentsCreditCard = selectedCreditCardPlan
        ? selectedCreditCardPlan.installment.filter(
            (item) => item.status === PaymentInstallmentStatus.PENDING,
          ).length
        : 0;
      const currentMonthlyPaymentCreditCard = selectedCreditCardPlan
        ? selectedCreditCardPlan.installmentPlan.totalAmount /
          selectedCreditCardPlan.installmentPlan.numberOfInstallments
        : 0;
      const additionalMonthlyPaymentCreditCard =
        remainingPaymentsCreditCard > 0
          ? newAmountToAdd / remainingPaymentsCreditCard
          : newAmountToAdd;
      const newMonthlyPaymentCreditCard =
        currentMonthlyPaymentCreditCard + additionalMonthlyPaymentCreditCard;

      return (
        <div style={formContainerStyle}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <label className="mb-2 font-medium">בחר הוראת קבע קיימת</label>
              <Dropdown
                style={inputStyle}
                value={selectedExistingInstallmentPlanId}
                options={existingCreditCardInstallments.map((plan) => {
                  const remaining = plan.installment.filter(
                    (item) => item.status === PaymentInstallmentStatus.PENDING,
                  ).length;
                  const monthlyPayment =
                    plan.installmentPlan.totalAmount /
                    plan.installmentPlan.numberOfInstallments;
                  return {
                    label: `₪${monthlyPayment.toFixed(0)}/חודש - ${remaining} תשלומים נותרו - נוצר ב-${new Date(plan.installmentPlan.createdAt).toLocaleDateString('he-IL')}`,
                    value: plan.installmentPlan.id,
                  };
                })}
                onChange={(e) =>
                  onSelectedExistingInstallmentPlanIdChange(e.value)
                }
                placeholder="בחר הוראת קבע"
              />
            </div>

            {selectedCreditCardPlan && (
              <div
                style={{
                  backgroundColor: '#FCE4EC',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #F48FB1',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <i
                    className="pi pi-info-circle"
                    style={{ color: '#C2185B', fontSize: '20px' }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#C2185B',
                      fontSize: '16px',
                    }}
                  >
                    פרטי הוספה להוראת קבע
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#424242',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>תשלומים נותרים:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {remainingPaymentsCreditCard}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>תשלום חודשי נוכחי:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      ₪{currentMonthlyPaymentCreditCard.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #F48FB1',
                      paddingTop: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>סכום להוספה:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        ₪{newAmountToAdd.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                      }}
                    >
                      <span>תוספת לתשלום חודשי:</span>
                      <span style={{ fontWeight: 'bold', color: '#C2185B' }}>
                        +₪{additionalMonthlyPaymentCreditCard.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      borderTop: '2px solid #C2185B',
                      paddingTop: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>
                        תשלום חודשי חדש:
                      </span>
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: '#C2185B',
                          fontSize: '18px',
                        }}
                      >
                        ₪{newMonthlyPaymentCreditCard.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );

    case PaymentMethod.ExistingBankTransferInstallments:
      const newAmountToAddBank = allocationAmounts.reduce(
        (sum, alloc) => sum + (alloc.amount || 0),
        0,
      );
      const selectedBankTransferPlan = existingBankTransferInstallments.find(
        (plan) =>
          plan.installmentPlan.id === selectedExistingBankTransferPlanId,
      );
      const remainingPaymentsBank = selectedBankTransferPlan
        ? selectedBankTransferPlan.installment.filter(
            (item) => item.status === PaymentInstallmentStatus.PENDING,
          ).length
        : 0;
      const currentMonthlyPaymentBank = selectedBankTransferPlan
        ? selectedBankTransferPlan.installmentPlan.totalAmount /
          selectedBankTransferPlan.installmentPlan.numberOfInstallments
        : 0;
      const additionalMonthlyPaymentBank =
        remainingPaymentsBank > 0
          ? newAmountToAddBank / remainingPaymentsBank
          : newAmountToAddBank;
      const newMonthlyPaymentBank =
        currentMonthlyPaymentBank + additionalMonthlyPaymentBank;

      return (
        <div style={formContainerStyle}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <label className="mb-2 font-medium">בחר הוראת קבע קיימת</label>
              <Dropdown
                style={inputStyle}
                value={selectedExistingBankTransferPlanId}
                options={existingBankTransferInstallments.map((plan) => {
                  const remaining = plan.installment.filter(
                    (item) => item.status === PaymentInstallmentStatus.PENDING,
                  ).length;
                  const monthlyPayment =
                    plan.installmentPlan.totalAmount /
                    plan.installmentPlan.numberOfInstallments;
                  return {
                    label: `₪${monthlyPayment.toFixed(0)}/חודש - ${remaining} תשלומים נותרו - נוצר ב-${new Date(plan.installmentPlan.createdAt).toLocaleDateString('he-IL')}`,
                    value: plan.installmentPlan.id,
                  };
                })}
                onChange={(e) =>
                  onSelectedExistingBankTransferPlanIdChange(e.value)
                }
                placeholder="בחר הוראת קבע"
              />
            </div>

            {selectedBankTransferPlan && (
              <div
                style={{
                  backgroundColor: '#E8EAF6',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #9FA8DA',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <i
                    className="pi pi-info-circle"
                    style={{ color: '#303F9F', fontSize: '20px' }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#303F9F',
                      fontSize: '16px',
                    }}
                  >
                    פרטי הוספה להוראת קבע
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#424242',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>תשלומים נותרים:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {remainingPaymentsBank}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>תשלום חודשי נוכחי:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      ₪{currentMonthlyPaymentBank.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #9FA8DA',
                      paddingTop: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>סכום להוספה:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        ₪{newAmountToAddBank.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                      }}
                    >
                      <span>תוספת לתשלום חודשי:</span>
                      <span style={{ fontWeight: 'bold', color: '#303F9F' }}>
                        +₪{additionalMonthlyPaymentBank.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      borderTop: '2px solid #303F9F',
                      paddingTop: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>
                        תשלום חודשי חדש:
                      </span>
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: '#303F9F',
                          fontSize: '18px',
                        }}
                      >
                        ₪{newMonthlyPaymentBank.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );

    default:
      return null;
  }
};
